// Code churn rate detail (docs/plans/churn-rate.md): of the lines shipped in
// each cohort, the fraction removed again within the churn window — AI vs human
// as the headline chart, self/cross folded in below as a breakdown.
//
// UNLIKE every other detail, churn does NOT follow the top-level 7d/14d/30d/90d
// window (a cohort only counts once its window has fully elapsed — the maturity
// gate). Its time controls (Cohorts span + the selectable window T) live in the
// top selector row instead (kpis.ts renderChurnWindow), swapped in whenever the
// churn detail is open. The "through <date>" label discloses the trailing horizon.
import { state, $, esc, usd, num, SR_PALETTE, get, dayOf, scopeQs } from '../core'
import { lineChart, stackChart } from '../charts'

export function renderChurn() {
  $('#metric-detail').innerHTML =
    '<div class="metric-head">' +
      '<h2>Code churn rate</h2>' +
      // AI share of shipped code — the adoption denominator that gives the
      // churn comparison its context (AI churn 15% reads differently at 5%
      // adoption vs 60%). Filled once the payload lands.
      '<span class="card-note" id="ch-share"></span>' +
    '</div>' +
    '<div class="panel">' +
      '<div class="sr-controls" id="ch-controls"></div>' +
      '<div class="chart-title" id="ch-title"></div>' +
      '<div id="ch-chart"></div>' +
      '<div class="sr-legend" id="ch-legend"></div>' +
      // Self- vs cross-churn, folded in as a secondary breakdown of each cohort's
      // churn (not a chart-swapping toggle): who tore the code out is a different
      // question from whose code it was, so both axes stay on screen at once.
      '<div class="ch-breakdown" id="ch-breakdown"></div>' +
      '<div class="card-note" id="ch-note"></div>' +
    '</div>' +
    '<div class="panel">' +
      '<div class="chart-title-row"><div class="chart-title">Churn hotspots</div>' +
        '<span class="seg" id="ch-hot-by"></span></div>' +
      '<div id="ch-hotspots"></div>' +
      '<div class="card-note" id="ch-hot-note"></div>' +
    '</div>' +
    '<div class="panel">' +
      '<div class="chart-title">Churned AI spend</div>' +
      '<div id="ch-spend-chart"></div>' +
      '<div class="sr-legend" id="ch-spend-legend"></div>' +
      '<div class="card-note" id="ch-spend-note"></div>' +
    '</div>';
  renderChurnControls();
  loadChurn();
}

function renderChurnControls() {
  // Only Bucket lives in-panel now; Cohorts + Window moved to the top selector
  // row (they're the metric's time filter — see the module header).
  var ch = state.churn;
  var bucketBtns = ['week', 'month'].map(function (b) {
    return '<button class="' + (b === ch.bucket ? 'on' : '') + '" data-b="' + b + '">' + b + '</button>';
  }).join('');
  $('#ch-controls').innerHTML =
    '<div class="sr-ctrl-row">' +
      '<span class="seg" id="ch-bucket">' + bucketBtns + '</span></div>';
  Array.prototype.forEach.call($('#ch-bucket').children, function (btn) {
    btn.onclick = function () { state.churn.bucket = btn.getAttribute('data-b'); renderChurnControls(); loadChurn(); };
  });
}

function loadChurn() {
  var winQs = state.churn.windowDays ? '&window=' + encodeURIComponent(state.churn.windowDays) : '';
  get('/api/churn?bucket=' + encodeURIComponent(state.churn.bucket) +
      '&range=' + encodeURIComponent(state.churn.range) +
      '&hotspots=' + encodeURIComponent(state.churn.hotspots) + winQs + scopeQs()).then(function (d) {
    if (!d || d.error) { $('#ch-chart').innerHTML = '<div class="empty">' + esc(d && d.error ? d.error : 'No data.') + '</div>'; return; }
    renderChurnChart(d);
    renderChurnBreakdown(d);
    renderChurnHotspots(d);
    renderAiShare(d);
    renderChurnedSpend(d);
  });
}

// The header context line: overall AI share of shipped code (all-time, from
// the attribution rollups), e.g. "AI wrote 42% of shipped lines (12,340 of
// 29,381)". Merge-anchored and ungated — a share is knowable at merge.
function renderAiShare(d) {
  var el = $('#ch-share');
  if (!el) return;
  var tot = (d.aiShare || []).reduce(function (a, r) {
    return { ai: a.ai + (r.addedAi || 0), all: a.all + (r.added || 0) };
  }, { ai: 0, all: 0 });
  // "All-time" is load-bearing, not filler: aiShare takes no cohort range, so
  // this number spans every merged PR while the panels below follow the Cohorts
  // selector. Unlabelled, a reader takes it for the same span as the chart.
  el.innerHTML = tot.all > 0
    ? esc('All-time, AI wrote ' + Math.round((tot.ai / tot.all) * 100) + '% of shipped lines (' + num(tot.ai) + ' of ' + num(tot.all) + ') - the adoption context behind the rates below.')
    : '';
}

// Churn in dollars: per matured cohort, the measured block-attributed AI spend
// as a stacked bar — full height = measured spend, filled = the churned slice.
function renderChurnedSpend(d) {
  var rows = d.spend || [];
  var totalAi = 0, totalChurned = 0;
  var points = rows.map(function (r) {
    totalAi += r.aiSpend || 0;
    totalChurned += r.churnedSpend || 0;
    return { bucket: r.bucket, total: r.aiSpend || 0, filled: r.churnedSpend || 0 };
  });
  // Amber fill (not the burn-chart emerald): the filled slice here is WASTED
  // spend — code that got torn out — so it should read as a warning, not a win.
  $('#ch-spend-chart').innerHTML = stackChart(d.buckets || [], points, 'usd', null, function (b, p) {
    return b + ': ' + usd(p.filled) + ' churned of ' + usd(p.total) + ' measured AI spend';
  }, '#b8860b');
  $('#ch-spend-legend').innerHTML = totalAi > 0
    ? '<span class="leg-overall">' + esc(usd(totalChurned) + ' of ' + usd(totalAi) + ' measured AI spend churned (' + Math.round((totalChurned / totalAi) * 100) + '%)') + '</span>'
    : '';
  $('#ch-spend-note').innerHTML = esc(
    'AI spend behind each cohort’s shipped lines and the slice whose code was torn out within ' +
    d.windowDays + ' days. Covers spend attributable to a block that authored ledger lines - a measured subset, not all AI spend.'
  );
}

function renderChurnChart(d) {
  // The one headline comparison: AI vs human, each rate over its own cohort's
  // lines (per-cohort denominators). Self/cross lives in the breakdown below.
  var lines = [
    cohortSeries(d, 'ai', 'AI', SR_PALETTE[0]),
    cohortSeries(d, 'human', 'human', SR_PALETTE[1]),
  ];
  $('#ch-title').textContent = 'AI vs human churn rate by cohort ' + state.churn.bucket;
  $('#ch-chart').innerHTML = lineChart(d.buckets || [], lines, { adaptive: true });
  $('#ch-legend').innerHTML = lines.map(function (l) {
    var tot = l.points.reduce(function (a, p) { return { num: a.num + (p.num || 0), denom: a.denom + (p.denom || 0) }; }, { num: 0, denom: 0 });
    var rate = tot.denom > 0 ? Math.round((tot.num / tot.denom) * 100) + '%' : '—';
    return '<span class="leg"><span class="swatch" style="background:' + l.color + '"></span>' + esc(l.label) +
      ' <span class="sr-cnt">' + rate + ' · ' + num(tot.num) + '/' + num(tot.denom) + ' lines</span></span>';
  }).join('');
  $('#ch-note').innerHTML = esc(
    'Of the attributed lines merged in each ' + state.churn.bucket + ', the share removed again within ' +
    d.windowDays + ' days. Only matured cohorts count, so this runs through ' + dayOf(d.throughDate) +
    ' - set the span and window T in the top row. ' +
    'Lines written by agents via shell commands count as human, so the AI rate is a floor.'
  );
}

// Self- vs cross-churn as a text decomposition of the churn already charted: of
// each cohort's churned lines, the share torn out by a different author (cross —
// the stronger smell) vs the same one. Unknown-author removals count in churned
// but neither split, so self+cross ≤ churned.
function renderChurnBreakdown(d) {
  var el = $('#ch-breakdown');
  if (!el) return;
  var agg = { ai: { churned: 0, self: 0, cross: 0 }, human: { churned: 0, self: 0, cross: 0 } };
  (d.rows || []).forEach(function (r) {
    var a = agg[r.cohort];
    if (!a) return;
    a.churned += r.churned || 0;
    a.self += r.selfChurned || 0;
    a.cross += r.crossChurned || 0;
  });
  var line = function (label, a) {
    if (!a.churned) return '';
    var pct = function (n) { return Math.round((n / a.churned) * 100) + '%'; };
    return '<div class="ch-bd-row"><span class="ch-bd-lbl">Of churned ' + esc(label) + ' lines</span>' +
      '<span class="ch-bd-val">' + pct(a.cross) + ' reverted by someone else · ' + pct(a.self) + ' reworked by the same author</span></div>';
  };
  var html = line('AI', agg.ai) + line('human', agg.human);
  el.innerHTML = html || '';
}

// Churn hotspots: the paths where shipped code is torn out most. A file|dir
// toggle in the panel header; each row is path + churn rate + churned/added,
// the bar scaled to the top row's rate so the worst offender fills the track.
function renderChurnHotspots(d) {
  var by = (d.hotspots && d.hotspots.by) || 'file';
  var seg = $('#ch-hot-by');
  if (seg) {
    seg.innerHTML = [['file', 'file'], ['dir', 'dir']].map(function (o) {
      return '<button class="' + (o[0] === by ? 'on' : '') + '" data-h="' + o[0] + '">' + o[1] + '</button>';
    }).join('');
    Array.prototype.forEach.call(seg.children, function (btn) {
      btn.onclick = function () { state.churn.hotspots = btn.getAttribute('data-h'); loadChurn(); };
    });
  }
  var rows = (d.hotspots && d.hotspots.rows) || [];
  var el = $('#ch-hotspots');
  if (!el) return;
  if (!rows.length) {
    el.innerHTML = '<div class="empty">Not enough matured lines per ' + esc(by) + ' yet.</div>';
  } else {
    var maxRate = rows.reduce(function (m, r) { var v = r.added > 0 ? r.churned / r.added : 0; return v > m ? v : m; }, 0) || 1;
    el.innerHTML = rows.map(function (r) {
      var rate = r.added > 0 ? r.churned / r.added : 0;
      var pct = Math.round((rate / maxRate) * 100);
      // The path label always carries its repo so a bare `src/x.ts` is never
      // ambiguous, and — when we know the repo's web base — deep-links to
      // GitHub's blob (file) or tree (dir) view at the default branch (HEAD
      // resolves it without knowing the branch name).
      var kind = by === 'dir' ? 'tree' : 'blob';
      var href = r.repoUrl
        ? r.repoUrl + '/' + kind + '/HEAD/' + r.path.split('/').map(encodeURIComponent).join('/')
        : null;
      var pathHtml = '<span class="ch-hot-repo">' + esc(r.repo) + '</span> ' + esc(r.path);
      var name = href
        ? '<a class="name ch-hot-link" href="' + esc(href) + '" target="_blank" rel="noopener noreferrer">' + pathHtml + '</a>'
        : '<span class="name">' + pathHtml + '</span>';
      // churned/added sample size in the hover title; the row shows just the rate.
      var title = r.repo + ' · ' + r.path + ' — ' + num(r.churned) + '/' + num(r.added) + ' lines';
      return '<div class="bar-row" title="' + esc(title) + '">' + name +
        '<span class="bar-track"><span class="bar-fill" style="width:' + pct + '%"></span></span>' +
        '<span class="n">' + Math.round(rate * 100) + '%</span></div>';
    }).join('');
  }
  $('#ch-hot-note').innerHTML = esc(
    'Top ' + (by === 'dir' ? 'directories' : 'files') + ' by churn rate over the selected cohorts (min 20 matured lines). ' +
    'Where rework concentrates - the first place to look after a bad overall rate.'
  );
}

function cohortSeries(d, cohort, label, color) {
  var byBucket = {};
  (d.rows || []).forEach(function (r) {
    if (r.cohort !== cohort) return;
    byBucket[r.bucket] = { num: r.churned, denom: r.added };
  });
  return {
    label: label, color: color,
    points: (d.buckets || []).map(function (b) {
      var p = byBucket[b] || { num: 0, denom: 0 };
      return { bucket: b, num: p.num, denom: p.denom, rate: p.denom > 0 ? p.num / p.denom : null };
    })
  };
}
